import { useCallback, useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  TimeInput,
} from '@accure/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle, Play } from 'lucide-react';

import type { CreateJobRequest } from '@/api/fe-api/domain/entities/capacity-evaluation';
import { CreateCapacityEvaluation } from '@/api/fe-api/view/mutations/create-capacity-evaluation';
import { DatePicker } from '@/components/form/inputs/date-picker';
import { NumberInput } from '@/components/form/inputs/number-input';
import { PowerUnit } from '@/entities/power-unit';
import { useCurrentAsset } from '@/hooks/use-current-asset';
import { useNow } from '@/hooks/useNow';
import { formStyle } from '@/styles/form-style';
import { energyLabels, powerLabels } from '@/utils/format/battery';
import { siteLabel } from '@/utils/format/customer';
import { toastResult } from '@/utils/toast';

import { combineDateAndTime, getLocalTimezoneAbbreviation } from '../utils';

import { ButtonDataExplorer } from './button-data-explorer';
import { type CreateTestInputs, createTestInputsSchema } from './form-schema';

export type CapacityTestFormCreateProps = {
  className?: string;
};
export function CapacityTestFormCreate({
  className,
}: CapacityTestFormCreateProps) {
  const createCapacityEvaluation = CreateCapacityEvaluation.useMutation();
  const { asset } = useCurrentAsset();
  const style = formStyle();
  const now = useNow();

  const getDefaultFormValues = useCallback(() => {
    const today = new Date();
    return {
      chargeStartDate: today,
      chargeStartTimeUtc: '',
      chargeEndDate: today,
      chargeEndTimeUtc: '',
      dischargeStartDate: today,
      dischargeStartTimeUtc: '',
      dischargeEndDate: today,
      dischargeEndTimeUtc: '',
    };
  }, []);

  const form = useForm<CreateTestInputs>({
    resolver: zodResolver(createTestInputsSchema),
    mode: 'onChange',
    defaultValues: getDefaultFormValues(),
  });

  const { chargeStartDate, dischargeStartDate, dischargeEndDate } = useWatch({
    control: form.control,
  });

  useEffect(() => {
    form.reset(getDefaultFormValues());
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [asset?.id]);

  const updateDates = useCallback(() => {
    if (chargeStartDate) {
      const updateField = (
        fieldName: 'chargeEndDate' | 'dischargeStartDate' | 'dischargeEndDate',
      ) => {
        const currentValue = form.getValues(fieldName);
        if (currentValue && currentValue < chargeStartDate) {
          form.setValue(fieldName, chargeStartDate);
        }
      };

      updateField('chargeEndDate');
      updateField('dischargeStartDate');
      updateField('dischargeEndDate');
    }

    // Ensure discharge end date is not before discharge start date
    if (
      dischargeStartDate &&
      dischargeEndDate &&
      dischargeEndDate < dischargeStartDate
    ) {
      form.setValue('dischargeEndDate', dischargeStartDate);
    }
  }, [chargeStartDate, dischargeStartDate, dischargeEndDate, form]);

  useEffect(() => {
    updateDates();
  }, [updateDates]);

  async function handleOnSubmit(data: CreateTestInputs) {
    if (!asset) {
      console.error('No asset selected');
      return;
    }

    const input: CreateJobRequest = {
      assetId: asset?.id,
      chargeStartTime: combineDateAndTime(
        data.chargeStartDate,
        data.chargeStartTimeUtc,
      ),
      chargeEndTime: combineDateAndTime(
        data.chargeEndDate,
        data.chargeEndTimeUtc,
      ),
      dischargeStartTime: combineDateAndTime(
        data.dischargeStartDate,
        data.dischargeStartTimeUtc,
      ),
      dischargeEndTime: combineDateAndTime(
        data.dischargeEndDate,
        data.dischargeEndTimeUtc,
      ),
      chargeEnergy: data.chargeEnergy,
      chargePower: data.chargePower,
      dischargeEnergy: data.dischargeEnergy,
      dischargePower: data.dischargePower,
    };

    const result = await toastResult(
      createCapacityEvaluation.mutateAsync({ data: input }),
      {
        success: 'Capacity Test created',
        loading: 'Submitting...',
        error: 'Failed to create a test',
        exception: 'Failed to create scenario',
      },
    );

    if (result.isOk()) {
      form.reset(getDefaultFormValues());
    }
  }

  const getTimezone = useCallback(
    (date: Date | null) => {
      if (date) {
        return getLocalTimezoneAbbreviation(date);
      }

      return getLocalTimezoneAbbreviation(now);
    },
    [now],
  );

  return (
    <div className={className}>
      <div className="space-y-6">
        <div>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-800">
              {asset
                ? `Run a Capacity Test – ${siteLabel(asset.fullFrontendId)}`
                : 'Run a Capacity Test'}
            </h2>
          </div>
          <div className="space-y-3 rounded-lg border border-zinc-200 bg-zinc-100 p-4 leading-relaxed text-gray-600">
            <p>
              ACCURE will analyze the selected time range, process the data, and
              automatically update SOH charts if the results are valid — giving
              you fast, accurate feedback on asset health.
            </p>
          </div>
        </div>
        <Form {...form}>
          <form
            key={asset?.id}
            className="space-y-6"
            onSubmit={form.handleSubmit(handleOnSubmit)}
          >
            <div className={style.inputSection()}>
              <h3 className={style.inputSectionTitle()}>Charge details</h3>

              <div className={style.inputSectionRow()}>
                <FormField
                  control={form.control}
                  name="chargeEnergy"
                  render={({ field }) => (
                    <FormItem className="gap-3">
                      <FormLabel className="text-neutral-700">
                        Targeted Energy
                        <span className={style.labelNote()}>(optional)</span>
                      </FormLabel>
                      <FormControl>
                        <NumberInput
                          className={style.input()}
                          rightIcon={
                            <span className={style.inputUnits()}>
                              {energyLabels[PowerUnit.W]}
                            </span>
                          }
                          value={field.value}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="chargePower"
                  render={({ field }) => (
                    <FormItem className="gap-3">
                      <FormLabel className="text-neutral-700">
                        Targeted Power
                        <span className={style.labelNote()}>(optional)</span>
                      </FormLabel>
                      <FormControl>
                        <NumberInput
                          className={style.input()}
                          rightIcon={
                            <span className={style.inputUnits()}>
                              {powerLabels[PowerUnit.W]}
                            </span>
                          }
                          value={field.value}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className={style.inputSectionRow()}>
                <div className="space-y-2">
                  <FormField
                    control={form.control}
                    name="chargeStartDate"
                    render={({ field }) => (
                      <FormItem className="gap-3">
                        <FormLabel className="text-neutral-700">
                          Start date and time{' '}
                          <span className="text-red-500">*</span>
                          <span className={style.labelNote()}>
                            ({getTimezone(field.value)})
                          </span>
                        </FormLabel>
                        <FormControl>
                          <DatePicker
                            className={style.input()}
                            value={field.value}
                            onSelect={field.onChange}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="chargeStartTimeUtc"
                    render={({ field }) => (
                      <FormItem className="gap-3">
                        <FormControl>
                          <TimeInput
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="HH:MM"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="space-y-2">
                  <FormField
                    control={form.control}
                    name="chargeEndDate"
                    render={({ field }) => (
                      <FormItem className="gap-3">
                        <FormLabel className="text-neutral-700">
                          End date and time{' '}
                          <span className="text-red-500">*</span>
                          <span className={style.labelNote()}>
                            ({getTimezone(field.value)})
                          </span>
                        </FormLabel>
                        <FormControl>
                          <DatePicker
                            className={style.input()}
                            value={field.value}
                            onSelect={field.onChange}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="chargeEndTimeUtc"
                    render={({ field }) => (
                      <FormItem className="gap-3">
                        <FormControl>
                          <TimeInput
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="HH:MM"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <ButtonDataExplorer
                  startDate={form.watch('chargeStartDate')}
                  endDate={form.watch('chargeEndDate')}
                />
              </div>
            </div>

            <div className={style.inputSection()}>
              <h3 className={style.inputSectionTitle()}>Discharge details</h3>

              <div className={style.inputSectionRow()}>
                <FormField
                  control={form.control}
                  name="dischargeEnergy"
                  render={({ field }) => (
                    <FormItem className="gap-3">
                      <FormLabel className="text-neutral-700">
                        Targeted Energy
                        <span className={style.labelNote()}>(optional)</span>
                      </FormLabel>
                      <FormControl>
                        <NumberInput
                          className={style.input()}
                          rightIcon={
                            <span className={style.inputUnits()}>
                              {energyLabels[PowerUnit.W]}
                            </span>
                          }
                          value={field.value}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="dischargePower"
                  render={({ field }) => (
                    <FormItem className="gap-3">
                      <FormLabel className="text-neutral-700">
                        Targeted Power
                        <span className={style.labelNote()}>(optional)</span>
                      </FormLabel>
                      <FormControl>
                        <NumberInput
                          className={style.input()}
                          rightIcon={
                            <span className={style.inputUnits()}>
                              {powerLabels[PowerUnit.W]}
                            </span>
                          }
                          value={field.value}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className={style.inputSectionRow()}>
                <div className="space-y-2">
                  <FormField
                    control={form.control}
                    name="dischargeStartDate"
                    render={({ field }) => (
                      <FormItem className="gap-3">
                        <FormLabel className="text-neutral-700">
                          Start date and time{' '}
                          <span className="text-red-500">*</span>
                          <span className={style.labelNote()}>
                            ({getTimezone(field.value)})
                          </span>
                        </FormLabel>
                        <FormControl>
                          <DatePicker
                            className={style.input()}
                            value={field.value}
                            onSelect={field.onChange}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="dischargeStartTimeUtc"
                    render={({ field }) => (
                      <FormItem className="gap-3">
                        <FormControl>
                          <TimeInput
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="HH:MM"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="space-y-2">
                  <FormField
                    control={form.control}
                    name="dischargeEndDate"
                    render={({ field }) => (
                      <FormItem className="gap-3">
                        <FormLabel className="text-neutral-700">
                          End date and time{' '}
                          <span className="text-red-500">*</span>
                          <span className={style.labelNote()}>
                            ({getTimezone(field.value)})
                          </span>
                        </FormLabel>
                        <FormControl>
                          <DatePicker
                            className={style.input()}
                            value={field.value}
                            onSelect={field.onChange}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="dischargeEndTimeUtc"
                    render={({ field }) => (
                      <FormItem className="gap-3">
                        <FormControl>
                          <TimeInput
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="HH:MM"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <ButtonDataExplorer
                  startDate={form.watch('dischargeStartDate')}
                  endDate={form.watch('dischargeEndDate')}
                />
              </div>
            </div>

            <div className="mt-8 space-y-3">
              {!form.formState.isValid && (
                <div className="rounded-lg border border-zinc-200 bg-zinc-100 px-4 py-3">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="mt-0.5 size-5 shrink-0 text-gray-600" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-800">
                        Complete the form to run your test
                      </p>
                      <p className="mt-1 text-sm text-gray-600">
                        All date and time fields are required. Enter the exact
                        charge and discharge windows for your capacity test.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {!form.formState.isValid &&
                Object.keys(form.formState.errors).length > 0 && (
                  <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="mt-0.5 size-5 shrink-0 text-red-600" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-red-800">
                          Please fix the following issues:
                        </p>
                        <ul className="mt-2 space-y-1 text-sm text-red-700">
                          {form.formState.errors.chargeStartTimeUtc && (
                            <li>
                              •{' '}
                              {form.formState.errors.chargeStartTimeUtc
                                .message || 'Charge start time is required'}
                            </li>
                          )}
                          {form.formState.errors.chargeEndTimeUtc && (
                            <li>
                              •{' '}
                              {form.formState.errors.chargeEndTimeUtc.message ||
                                'Charge end time is required'}
                            </li>
                          )}
                          {form.formState.errors.dischargeStartTimeUtc && (
                            <li>
                              •{' '}
                              {form.formState.errors.dischargeStartTimeUtc
                                .message || 'Discharge start time is required'}
                            </li>
                          )}
                          {form.formState.errors.dischargeEndTimeUtc && (
                            <li>
                              •{' '}
                              {form.formState.errors.dischargeEndTimeUtc
                                .message || 'Discharge end time is required'}
                            </li>
                          )}
                          {form.formState.errors.chargeStartDate && (
                            <li>
                              •{' '}
                              {form.formState.errors.chargeStartDate.message ||
                                'Charge start date is required'}
                            </li>
                          )}
                          {form.formState.errors.chargeEndDate && (
                            <li>
                              •{' '}
                              {form.formState.errors.chargeEndDate.message ||
                                'Charge end date is required'}
                            </li>
                          )}
                          {form.formState.errors.dischargeStartDate && (
                            <li>
                              •{' '}
                              {form.formState.errors.dischargeStartDate
                                .message || 'Discharge start date is required'}
                            </li>
                          )}
                          {form.formState.errors.dischargeEndDate && (
                            <li>
                              •{' '}
                              {form.formState.errors.dischargeEndDate.message ||
                                'Discharge end date is required'}
                            </li>
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

              <div className="flex justify-center">
                <Button
                  type="submit"
                  size="lg"
                  disabled={!form.formState.isValid}
                >
                  <Play />
                  Run test
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}
